import React, { useState, useEffect, useMemo, memo } from "react"
import Box from "@material-ui/core/Box"
import { makeStyles } from "@material-ui/core/styles"
import ErrorAccess from "@/components/common/ErrorAccess"
import Loading from "@/components/common/Loading"
import RequestSnackbar from "@/components/common/RequestSnackbar"
import AssessmentMain from "@/components/assessment"
import useHandleRequestSnackbar from "@/hooks/useHandleRequestSnackbar"
import {
    getQuestionsByParticipantUUID,
    getResponsesByParticipantUUID,
    getChoicesByQuestionId,
    resendAssessmentLink,
} from "@/apisParticipantPublic"
import { useParams, useNavigate } from "react-router-dom"
import clsx from "clsx"
import { urlPath, shuffleArray } from "@/utils"
import { errorApiMessage, errorMessageClient, errorCode } from "@/constants"

const useStyles = makeStyles(() => ({}))

const Assessment = memo((props) => {
    const { headerHeight, participantDetail, participantCheckLoading, updateParticipantDetail } = props
    const classes = useStyles()
    const { participantUUID } = useParams()
    const requestSnackbarValue = useHandleRequestSnackbar(true)
    const [responsesLoading, setResponsesLoading] = useState({
        loading: true,
        error: false,
        message: "",
    })
    const [questionsDetailLoading, setQuestionsDetailLoading] = useState({
        loading: true,
        error: false,
        message: "",
    })
    const [responses, setResponses] = useState([])
    const [questionsDetail, setQuestionsDetail] = useState([])

    const navigate = useNavigate()

    // retrieved participant questions
    useEffect(() => {
        const retrieveQuestions = async (uuid) => {
            try {
                const participantQuestionsDetailData = await getQuestionsByParticipantUUID(uuid)
                const participantQuestionsDetail = participantQuestionsDetailData.data.questions
                const questionsDetailResults = await Promise.all(
                    participantQuestionsDetail.map(async (question) => {
                        const { questionId, questionText } = question
                        const participantChoicesDetailData = await getChoicesByQuestionId(questionId)
                        const participantChoicesDetail = participantChoicesDetailData.data[0].choices

                        const choiceNA = []
                        const choiceNormal = []
                        participantChoicesDetail.forEach((choice) => {
                            if (!choice.scoreType) {
                                choiceNormal.push(choice)
                            } else {
                                choiceNA.push(choice)
                            }
                        })
                        const choicesShuffle = [...shuffleArray(choiceNormal), ...choiceNA]

                        return {
                            question: {
                                questionId,
                                questionText,
                            },
                            choices: choicesShuffle,
                        }
                    })
                )
                setQuestionsDetail(questionsDetailResults)
                setQuestionsDetailLoading({ loading: false, error: false })
            } catch (error) {
                setQuestionsDetailLoading({
                    loading: false,
                    error: true,
                    message: error?.data?.message || "Loading questions meets an error",
                })
            }
        }
        const retrieveResponses = async (uuid) => {
            try {
                const participantResponsesDetailData = await getResponsesByParticipantUUID(uuid)
                if (participantResponsesDetailData.data.participant.length) {
                    const participantResponsesDetail = participantResponsesDetailData.data.participant[0].responses
                    setResponses(participantResponsesDetail)
                }
                setResponsesLoading({ loading: false, error: false })
            } catch (error) {
                setResponsesLoading({ loading: false, error: true, message: error.data?.message || "error" })
            }
        }
        retrieveQuestions(participantUUID)
        retrieveResponses(participantUUID)
    }, [participantUUID])

    const assessmentError = useMemo(() => {
        if (participantCheckLoading.message === errorApiMessage.participantNotFound) {
            return <ErrorAccess message={errorMessageClient.participantNotFound} />
        }
        if (participantCheckLoading.errorCode === errorCode.participantPublicAccessTokenInvalid) {
            return (
                <ErrorAccess
                    message="send new invitation link"
                    buttonText="send"
                    buttonCallback={() => resendAssessmentLink(participantUUID)}
                />
            )
        }
        return <ErrorAccess />
    }, [participantUUID, participantCheckLoading])

    const roleOptOutError = useMemo(() => {
        return <ErrorAccess message="You have opted out" subMessageNormal="Please contact your project sponsor if you have any questions" />
    }, [])

    const noRoleError = useMemo(() => {
        const token = JSON.parse(localStorage.getItem("participantPublicAccessToken"))
        return (
            <ErrorAccess
                message="role not set"
                buttonText="set up role"
                buttonCallback={() => {
                    navigate(urlPath("invitation-participant", undefined, { participantUUID, token }))
                }}
            />
        )
    }, [participantUUID])

    const assessmentLaunchError = useMemo(() => <ErrorAccess message="Project has been completed" />, [])

    const assessmentMain = (
        <Box className="assessment-wrapper">
            {(questionsDetailLoading.loading || responsesLoading.loading) && (
                <Box className={clsx(classes.contentWrapper, classes.loading)}>
                    <Loading content="Loading questions, choices and Participants responses ..." transformX={180} />
                </Box>
            )}
            {!(questionsDetailLoading.loading || responsesLoading.loading) && (responsesLoading.error || questionsDetailLoading.error) && (
                <>
                    <RequestSnackbar
                        status={false}
                        message={responsesLoading.message || questionsDetailLoading.message}
                        snackbarOpen={requestSnackbarValue.snackbarOpen}
                        handleSnackbarOpenStatus={requestSnackbarValue.handleSnackbarOpenStatus}
                    />
                    <ErrorAccess />
                </>
            )}
            {!(questionsDetailLoading.loading || responsesLoading.loading) && !(responsesLoading.error || questionsDetailLoading.error) && (
                <AssessmentMain
                    headerHeight={headerHeight}
                    questionsDetail={questionsDetail}
                    responses={responses}
                    participantDetail={participantDetail}
                    updateParticipantDetail={updateParticipantDetail}
                />
            )}
        </Box>
    )

    const output = () => {
        if (participantCheckLoading.error) return assessmentError
        if (participantDetail.roleId === 0) return roleOptOutError
        if (!participantDetail.roleId) return noRoleError
        if (participantDetail.projects.projectLaunchDate) return assessmentLaunchError
        return assessmentMain
    }

    return <>{output()}</>
})

export default Assessment
